import React, { createContext, useContext, useEffect, useState } from "react";

// PRODUÇÃO
const API_URL = "https://apitigdig13.biti9.ai";
const CLIENT_ID = "3da0d0b8-7d8d-4244-b23f-427cf8a80f51";
const TENANT_ID = "963ca415-7dad-4569-b6df-554fbb071304";
const REDIRECT_URI = "https://apitigdig13.biti9.ai/auth/callback"; // Callback no FastAPI
const AUTHORIZE_URL = `https://login.microsoftonline.com/${TENANT_ID}/oauth2/v2.0/authorize`;

const AuthContext = createContext(null);

export function useAuthFetch() {
  const ctx = useContext(AuthContext);
  return ctx?.authFetch;
}

// Helpers de PKCE
function base64UrlEncode(bytes) {
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function generatePkcePair() {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  const codeVerifier = base64UrlEncode(array);

  const data = new TextEncoder().encode(codeVerifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  const codeChallenge = base64UrlEncode(new Uint8Array(digest));

  return { codeVerifier, codeChallenge };
}

export async function authFetch(input, init = {}) {
  const options = {
    ...init,
    credentials: "include", 
  };

  let response = await fetch(input, options);

  // Se o token expirar durante o uso do app
  if (response.status === 401) {
    try {
      const refreshRes = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });

      if (refreshRes.ok) {
        // Tenta a requisição original novamente
        response = await fetch(input, options);
      } else {
        // Refresh token também expirou (sessão de 30 dias acabou)
        throw new Error("Sessão expirada.");
      }
    } catch (err) {
      throw err;
    }
  }

  return response;
}

// DEV: o login via Entra ID não funciona em localhost — o cookie do code_verifier
// (PKCE) é fixado no domínio .biti9.ai e o navegador não grava esse cookie quando a
// página está em localhost, causando o erro AADSTS50148 (code_verifier mismatch).
// Por isso o LoginGate real (comentado abaixo) foi trocado por esse bypass, que
// libera o acesso direto sem exigir login do Entra ID.
// ANTES DE SUBIR PARA PRODUÇÃO: comente esta função e descomente o LoginGate original abaixo.
// function LoginGate({ children }) {
//   return (
//     <AuthContext.Provider value={{ authFetch }}>
//       {children}
//     </AuthContext.Provider>
//   );
// }

// PRODUÇÃO: LoginGate original com o fluxo real de login via Entra ID (PKCE)
function LoginGate({ children }) {
  const [status, setStatus] = useState("checking");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    // 1. Captura erros de OAuth vindos da URL
    const urlParams = new URLSearchParams(window.location.search);
    const oauthError = urlParams.get("oauth_error");
    if (oauthError) {
      setErrorMsg(oauthError);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // 2. Checa se o usuário está autenticado no carregamento da página
    async function checkAuth() {
      try {
        const res = await fetch(`${API_URL}/auth/me`, { credentials: "include" });

        if (res.ok) {
          const data = await res.json();
          if (data.authenticated) {
            setStatus("authenticated");
            return;
          }
        }

        // Se /auth/me falhar ou retornar falso, tenta renovar no /auth/refresh
        const refreshRes = await fetch(`${API_URL}/auth/refresh`, {
          method: "POST",
          credentials: "include",
        });

        if (refreshRes.ok) {
          setStatus("authenticated");
        } else {
          setStatus("anonymous");
        }
      } catch {
        setStatus("anonymous");
      }
    }

    checkAuth();
  }, []);

  const handleLogin = async () => {
    const { codeVerifier, codeChallenge } = await generatePkcePair();

    const isHttps = window.location.protocol === "https:";
    document.cookie = `pkce_code_verifier=${encodeURIComponent(codeVerifier)}; path=/; domain=.biti9.ai; max-age=600; samesite=lax${isHttps ? "; secure" : ""}`;

    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      response_type: "code",
      redirect_uri: REDIRECT_URI,
      response_mode: "query",
      scope: "openid profile email offline_access",
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
      prompt: "select_account",
    });

    window.location.href = `${AUTHORIZE_URL}?${params.toString()}`;
  };

  if (status === "checking") {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <p className="text-slate-600 font-medium">Verificando sessão...</p>
      </div>
    );
  }

  if (status === "authenticated") {
    return (
      <AuthContext.Provider value={{ authFetch }}>
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <div className="flex flex-col h-screen items-center justify-start bg-slate-50 pt-20">
      <img src="logo-tigre.png" alt="logo-tigre" className="w-48" />
      <div className="rounded-xl bg-white p-10 shadow-md text-center max-w-md">
        <h1 className="text-xl font-semibold text-slate-800 mb-2">
          Quebra de PDF's Tigre
        </h1>
        <p className="text-slate-500 mb-6">Faça login com sua conta corporativa.</p>
        {errorMsg && <p className="text-sm text-red-600 mb-4">{errorMsg}</p>}
        <button
          onClick={handleLogin}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2 rounded-lg transition"
        >
          Entrar com Microsoft
        </button>
      </div>
      <img
        src="logo-biti9.png"
        alt="logo-biti9"
        className="fixed bottom-4 right-4 w-24"
      />
    </div>
  );
}

export default function AuthProvider({ children }) {
  return <LoginGate>{children}</LoginGate>;
  
}